import clsx from 'clsx';
import Image from 'next/image';
import { Typography } from '~/shared/components';
import styles from './ProductInfoLabel.module.scss';

/** Every label icon is drawn to the same height; only the width differs per artwork. */
export const PRODUCT_INFO_LABEL_ICON_HEIGHT = 32;

export interface ProductInfoLabelProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Path to the icon, e.g. `/assets/energylabels/a.svg`. */
    iconSrc: string;
    iconAlt: string;
    /** Intrinsic width of the artwork. Only sets the aspect ratio; CSS keeps the height fixed. */
    iconWidth: number;
    iconHeight?: number;
    /** Accessible name of the icon button, e.g. "Åbn energimærke". */
    iconLabel: string;
    /** Opens the label's dialog. Left out when the product has no image to open, which
     * disables the button rather than hiding the icon. */
    onIconClick?: () => void;
    /** Text beside the icon. Omitted entirely when there is nothing to say. */
    text?: string;
    onTextClick?: () => void;
}

/**
 * The grey pill that carries a product label: a clickable icon and an optional action beside it.
 * Purely presentational, so each label owns where its icon, text and click targets come from.
 */
export const ProductInfoLabel: React.FC<ProductInfoLabelProps> = ({
    className,
    iconSrc,
    iconAlt,
    iconWidth,
    iconHeight = PRODUCT_INFO_LABEL_ICON_HEIGHT,
    iconLabel,
    onIconClick,
    text,
    onTextClick,
    ...rest
}) => (
    <div className={clsx(styles.productInfoLabel, className)} {...rest}>
        <button
            type="button"
            className={styles.productInfoLabelButton}
            onClick={onIconClick}
            disabled={!onIconClick}
            aria-label={iconLabel}
            aria-haspopup="dialog"
        >
            <Image
                className={styles.productInfoLabelImage}
                src={iconSrc}
                width={iconWidth}
                height={iconHeight}
                alt={iconAlt}
            />
        </button>

        {!!text && (
            <div className={styles.productInfoLabelText}>
                <button
                    type="button"
                    className={styles.productInfoLabelLink}
                    onClick={onTextClick}
                    disabled={!onTextClick}
                >
                    <Typography as="span" variant="paragraph-S">
                        {text}
                    </Typography>
                </button>
            </div>
        )}
    </div>
);
